import type { GameStateSnapshot, PlayerId, RoomId } from '@juegazo/shared';
import { io, type Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
} from '@juegazo/shared';

export type ClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export interface SessionInfo {
  roomId: RoomId | null;
  playerId: PlayerId | null;
  displayName: string | null;
}

const SESSION_KEY = 'juegazo.session';

/**
 * Traduce mensajes de error del servidor a versiones amigables para el usuario.
 * El servidor devuelve códigos técnicos (`PARTIDA_NO_EXISTE`, etc.) y mensajes
 * crudos en inglés desde `game-core` que no deberían filtrarse a la UI tal cual.
 */
const SERVER_ERROR_MESSAGES: Record<string, string> = {
  // Códigos del handler de room:rejoin (apps/server/src/index.ts)
  PARTIDA_NO_EXISTE: 'La sala no existe o ya no está disponible',
  PARTIDA_EN_CURSO: 'La partida ya comenzó',
  'Sala no encontrada': 'La sala no existe',
  'Jugador no encontrado': 'No se encontró al jugador en la sala',
  'Estado inválido': 'El estado del juego es inválido',
  // Mensajes de game-core que pueden llegar vía room:error
  'Cannot pass bomb in current state': 'No podés pasar la bomba en este momento',
  'Bomb already exploded': 'La bomba ya explotó',
  'Can only resume from paused': 'Solo se puede reanudar una partida en pausa',
  'No active prompt': 'No hay una consigna activa',
  'Return window expired': 'Se acabó el tiempo para devolver el turno',
};

export function translateServerError(message: string | undefined | null): string {
  if (!message) return 'No se pudo conectar con la sala';
  return SERVER_ERROR_MESSAGES[message] ?? message;
}

export const loadSession = (): SessionInfo => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return { roomId: null, playerId: null, displayName: null };
    return JSON.parse(raw) as SessionInfo;
  } catch {
    return { roomId: null, playerId: null, displayName: null };
  }
};

export const saveSession = (info: SessionInfo) => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(info));
};

export const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
};

export const createSocket = (): ClientSocket => {
  const url = window.location.origin;
  return io(url, {
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: 10,
    transports: ['websocket', 'polling'],
  });
};

export interface GameStoreState {
  connected: boolean;
  state: GameStateSnapshot | null;
  lastError: { code: string; message: string } | null;
  lastFinished: import('@juegazo/shared').FinalResult | null;
  lastExplosion: { playerId: PlayerId; displayName: string } | null;
  lastPlayerLeft: { playerId: PlayerId; displayName: string; reason: 'leave' | 'disconnect' } | null;
  lastPlayerReconnected: { playerId: PlayerId; displayName: string } | null;
}

export type GameStoreListener = (state: GameStoreState) => void;

export class GameStore {
  private socket: ClientSocket | null = null;
  private state: GameStoreState = {
    connected: false,
    state: null,
    lastError: null,
    lastFinished: null,
    lastExplosion: null,
    lastPlayerLeft: null,
    lastPlayerReconnected: null,
  };
  private listeners = new Set<GameStoreListener>();
  private wasDisconnected = false;
  private readonly createSocketFn: () => ClientSocket;

  constructor(opts: { createSocket?: () => ClientSocket } = {}) {
    this.createSocketFn = opts.createSocket ?? createSocket;
  }

  connect() {
    if (this.socket) return;
    this.socket = this.createSocketFn();
    this.socket.on('connect', () => {
      this.update({ connected: true });
      const session = loadSession();
      if (session.roomId && session.playerId && session.displayName && (!this.state.state || this.wasDisconnected)) {
        let responded = false;
        const guard = setTimeout(() => {
          if (!responded) {
            clearSession();
            this.update({
              lastError: { code: 'reconnect_timeout', message: 'Tiempo agotado' },
              state: null,
            });
          }
        }, 5000);
        this.socket!.emit(
          'room:rejoin',
          { roomId: session.roomId, playerId: session.playerId, displayName: session.displayName },
          (resp) => {
            this.wasDisconnected = false;
            responded = true;
            clearTimeout(guard);
            if (resp.ok) {
              this.update({ state: resp.state, lastError: null });
            } else {
              clearSession();
              this.update({
                lastError: { code: 'reconnect_failed', message: translateServerError(resp.error) },
                state: null,
              });
            }
          },
        );
      }
    });
    this.socket.on('connect_error', (err) => {
      const message = err instanceof Error ? err.message : 'Error de transporte';
      this.update({
        lastError: { code: 'socket_connect_error', message },
      });
    });
    this.socket.io.on('reconnect_failed', () => {
      this.update({
        lastError: { code: 'socket_reconnect_failed', message: 'No se pudo reconectar' },
      });
    });
    this.socket.on('disconnect', () => {
      this.wasDisconnected = true;
      this.update({ connected: false });
    });
    this.socket.on('room:state', (snap) => this.update({ state: snap }));
    this.socket.on('game:state', (snap) => this.update({ state: snap }));
    this.socket.on('lobby:updated', (snap) => this.update({ state: snap }));
    this.socket.on('game:exploded', (info) => this.update({ lastExplosion: info }));
    this.socket.on('game:finished', (result) => this.update({ lastFinished: result }));
    this.socket.on('game:paused', () => {
      if (this.state.state) this.update({ state: { ...this.state.state, paused: true, state: 'paused' } });
    });
    this.socket.on('game:resumed', () => {
      if (this.state.state) this.update({ state: { ...this.state.state, paused: false, state: 'playing' } });
    });
    this.socket.on('player:left', (info) => this.update({ lastPlayerLeft: info }));
    this.socket.on('player:reconnected', (info) => this.update({ lastPlayerReconnected: info }));
    this.socket.on('room:error', (err) =>
      this.update({
        lastError: { code: err.code, message: translateServerError(err.message) },
      }),
    );
  }

  disconnect() {
    this.socket?.disconnect();
    this.socket = null;
  }

  getState = () => this.state;
  getSocket = () => this.socket;

  subscribe = (fn: GameStoreListener): (() => void) => {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  };

  /**
   * Limpia el snapshot del juego. Llamar cuando el usuario abandona una sala
   * para que las emissions tardías del servidor no redirijan la UI.
   */
  clearGameState() {
    this.update({ state: null, lastFinished: null, lastExplosion: null });
  }

  /**
   * Limpia el flag de "último jugador que se fue" para que el toast asociado
   * solo se muestre una vez, o para evitar mostrarlo si el jugador ya se
   * reconectó (visibilidad gestionada por app-root).
   */
  clearLastPlayerLeft() {
    if (this.state.lastPlayerLeft) this.update({ lastPlayerLeft: null });
  }

  /**
   * Limpia el flag de "último jugador que volvió a entrar" tras mostrar
   * el toast asociado para evitar mostrarlo dos veces.
   */
  clearLastPlayerReconnected() {
    if (this.state.lastPlayerReconnected) this.update({ lastPlayerReconnected: null });
  }

  private update(partial: Partial<GameStoreState>) {
    this.state = { ...this.state, ...partial };
    for (const l of this.listeners) l(this.state);
  }

  /**
   * Fuerza un ciclo disconnect + connect del socket sin recargar la página.
   * Equivalente programático a un F5 desde el punto de vista de la conexión,
   * pero conserva el estado de Lit y la session de localStorage.
   * El flag `wasDisconnected` se setea en true durante el disconnect y al
   * volver el connect se re-emite `room:rejoin` automáticamente.
   */
  forceReconnect(): void {
    const s = this.socket;
    if (!s) return;
    s.disconnect();
    s.connect();
  }

  send<E extends keyof ClientToServerEvents>(event: E, ...args: Parameters<ClientToServerEvents[E]>) {
    const s = this.socket;
    if (!s) return;
    (s.emit as (event: E, ...args: unknown[]) => void)(event, ...args);
  }

  createRoom(displayName: string): Promise<{ ok: true; roomId: RoomId; playerId: PlayerId } | { ok: false; error: string }> {
    return new Promise((resolve) => {
      this.send('room:create', { displayName }, (resp) => {
        resolve(resp);
      });
    });
  }

  joinRoom(roomId: RoomId, displayName: string): Promise<{ ok: true; playerId: PlayerId; state: GameStateSnapshot } | { ok: false; error: string }> {
    return new Promise((resolve) => {
      this.send('room:join', { roomId, displayName }, (resp) => resolve(resp));
    });
  }

  rejoinRoom(roomId: RoomId, playerId: PlayerId, displayName: string): Promise<{ ok: true; playerId: PlayerId; state: GameStateSnapshot } | { ok: false; error: string }> {
    return new Promise((resolve) => {
      this.send('room:rejoin', { roomId, playerId, displayName }, (resp) => resolve(resp));
    });
  }
}

export const gameStore = new GameStore();
